import { Plus, ArrowLeft, Loader2 } from "lucide-react";
import { useCallback, useMemo, useState, useEffect, useRef } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { NOTIFICATION_EVENTS } from "@/modules/dashboard/notifications/constants/notifications.constants";
import { notificationsSocketService } from "@/modules/dashboard/notifications/services/notifications-socket.service";
import { Button } from "@/shared/components/form/Button";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { ClientsService } from "../../clients/services/clientsService";
import type { ClientAccount } from "../../clients/types/clients-api.types";
import { ClientNotifiedModal } from "../../confirmations/components/ClientNotifiedModal";
import { NewConfirmationForm } from "../../confirmations/components/NewConfirmationForm";
import type { NewConfirmationFormData } from "../../confirmations/types/new-confirmation.types";
import type { NewConfirmationFormErrors } from "../../confirmations/utils/validateNewConfirmationForm";
import {
  validateNewConfirmationForm,
  translateNewConfirmationErrors,
  isValidMontoValue,
} from "../../confirmations/utils/validateNewConfirmationForm";
import { AttachmentsGalleryModal } from "../components/AttachmentsGalleryModal";
import { PaymentClientPayerFields } from "../components/PaymentClientPayerFields";
import { PaymentHistoryModal } from "../components/PaymentHistoryModal";
import { PaymentInfoModal } from "../components/PaymentInfoModal";
import { PaymentMethodFieldsSection } from "../components/PaymentMethodFieldsSection";
import {
  PaymentPayerAccountSection,
  type PayerAccountsPhase,
} from "../components/PaymentPayerAccountSection";
import { PaymentsBoard } from "../components/PaymentsBoard";
import { PendingTotalsSummary } from "../components/PendingTotalsSummary";
import { PAYMENTS_PAGE_SIZE } from "../constants/payments.constants";
import { paymentsService } from "../services/payments.service";
import type { NewPaymentFormFullData, PaymentMethodFields } from "../types/new-payment.types";
import type {
  PaymentStatusUpdateApi,
  PaymentListItem,
  PendingCurrencyTotal,
  OperationCurrency,
} from "../types/payments-api.types";
import type { Payment, PaymentStatus } from "../types/payments.types";
import {
  paymentMethodFieldsFromAccountMetadata,
  resolvePaymentBankAndCurrency,
} from "../utils/applyClientAccountToPaymentForm";
import { mapPaymentListItemToPayment } from "../utils/mapPaymentFromApi";
import { getDefaultPaymentFormData } from "../utils/paymentFormDefaults";
import { validatePaymentMethodFields } from "../utils/validatePaymentMethodFields";

function toAmountNumber(value: unknown): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function paymentStatusToApi(status: PaymentStatus): PaymentStatusUpdateApi {
  switch (status) {
    case "pending":
      return "PENDING";
    case "in_progress":
      return "IN_PROGRESS";
    case "paid":
      return "PAID";
    default:
      return "PENDING";
  }
}

export const PaymentsPage = () => {
  const { t } = useTranslations("payments");
  const { t: tGlobal } = useTranslations("global");
  const { user, isInternalUser, isAdminUser, can } = useAuth();
  /**
   * Mirrors the guard on `POST /internal/payments/:id/force-release`: an internal admin passes
   * unconditionally, anyone else needs admin-level rights on the module. Kept in sync on purpose — a narrower
   * check hides a legitimate action from an operator who holds it, and a wider one offers a button that ends
   * in 401.
   *
   * This is the revisit the previous note asked for. `scopes[]` now carries the RBAC-effective permissions,
   * which are atoms — `operations:payments:admin` never appears there, so checking it was dead code. The
   * backend expands `admin` to read+write+delete, and `delete` is the atom only an admin-level role grants,
   * so it is the faithful stand-in.
   */
  const canForceRelease = (isInternalUser && isAdminUser) || can("operations:payments:delete");
  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_operaciones") },
      { label: tGlobal("sidebar_menu_payments"), to: "/app/payout-requests" },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);
  const { t: tConfirmations } = useTranslations("confirmations");
  const [hasNoAccess, setHasNoAccess] = useState(false);
  const [pending, setPending] = useState<Payment[]>([]);
  const [inProgress, setInProgress] = useState<Payment[]>([]);
  const [paid, setPaid] = useState<Payment[]>([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [showNewPaymentSection, setShowNewPaymentSection] = useState(false);
  const [formData, setFormData] = useState<NewConfirmationFormData>(getDefaultPaymentFormData);
  const [methodFields, setMethodFields] = useState<PaymentMethodFields>({});
  const [commonErrors, setCommonErrors] = useState<NewConfirmationFormErrors>({});
  const [methodErrors, setMethodErrors] = useState<Partial<Record<string, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImageUrls, setGalleryImageUrls] = useState<string[]>([]);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyPaymentId, setHistoryPaymentId] = useState("");
  const [paymentInfoPaymentId, setPaymentInfoPaymentId] = useState("");
  const [confirmPaidForPaymentId, setConfirmPaidForPaymentId] = useState<string | null>(null);
  const [clientNotifiedModalOpen, setClientNotifiedModalOpen] = useState(false);
  const [clientNotifiedPaymentId, setClientNotifiedPaymentId] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingTotals, setPendingTotals] = useState<PendingCurrencyTotal[]>([]);
  const [pendingTotalsLoading, setPendingTotalsLoading] = useState(true);
  const [pendingTotalsError, setPendingTotalsError] = useState<string | null>(null);
  const [cancelledPaymentIdFromSocket, setCancelledPaymentIdFromSocket] = useState<string | null>(null);

  const [payerClientAccountIds, setPayerClientAccountIds] = useState<string[]>([]);
  const [payerAccounts, setPayerAccounts] = useState<ClientAccount[]>([]);
  const [payerAccountsPhase, setPayerAccountsPhase] = useState<PayerAccountsPhase>("idle");
  const [payerAccountsLoadError, setPayerAccountsLoadError] = useState<string | null>(null);
  const [selectedPayerAccountId, setSelectedPayerAccountId] = useState<string | null>(null);
  const [payerAccountSelectError, setPayerAccountSelectError] = useState<string | null>(null);

  const accountApplyLockRef = useRef(false);
  const prevCurrencyRef = useRef("");
  const prevBankRef = useRef("");
  const payerAccountsFetchSeq = useRef(0);
  const prevShowPaymentSectionRef = useRef(false);

  const handlePayerAccountIdsChange = useCallback((ids: string[]) => {
    setPayerClientAccountIds((prev) => {
      if (prev.length === ids.length && prev.every((value, i) => value === ids[i])) {
        return prev;
      }
      return [...ids];
    });
  }, []);

  const paymentInfoPaymentIdRef = useRef(paymentInfoPaymentId);
  paymentInfoPaymentIdRef.current = paymentInfoPaymentId;
  const historyPaymentIdRef = useRef(historyPaymentId);
  historyPaymentIdRef.current = historyPaymentId;
  const confirmPaidForPaymentIdRef = useRef(confirmPaidForPaymentId);
  confirmPaidForPaymentIdRef.current = confirmPaidForPaymentId;
  const clientNotifiedPaymentIdRef = useRef(clientNotifiedPaymentId);
  clientNotifiedPaymentIdRef.current = clientNotifiedPaymentId;
  const pendingRef = useRef<Payment[]>(pending);
  pendingRef.current = pending;

  const applyPendingTotalDelta = useCallback(
    (currency: OperationCurrency | string | null | undefined, delta: number) => {
      if (!currency || !Number.isFinite(delta) || delta === 0) return;
      setPendingTotals((prev) => {
        const idx = prev.findIndex((item) => item.currency === currency);
        if (idx === -1) {
          if (delta <= 0) return prev;
          return [
            ...prev,
            {
              currency: currency as OperationCurrency,
              total: delta,
            },
          ];
        }
        const next = prev.slice();
        const updatedTotal = next[idx].total + delta;
        if (updatedTotal <= 0.0000001) {
          next.splice(idx, 1);
        } else {
          next[idx] = { ...next[idx], total: updatedTotal };
        }
        return next;
      });
    },
    [],
  );

  const fetchPendingTotals = useCallback(async () => {
    setPendingTotalsLoading(true);
    setPendingTotalsError(null);
    try {
      const data = await paymentsService.fetchPendingTotalsByCurrency();
      setPendingTotals(Array.isArray(data) ? data : []);
    } catch (error) {
      if (HttpApiService.isAccessDeniedError(error)) {
        setPendingTotals([]);
        return;
      }
      const message = HttpApiService.getErrorMessage(error);
      setPendingTotalsError(message || t("pending_totals_error"));
    } finally {
      setPendingTotalsLoading(false);
    }
  }, [t]);

  const fetchPayments = useCallback(async () => {
    try {
      setHasNoAccess(false);
      const response = await paymentsService.fetchList({
        skip: 0,
        limit: PAYMENTS_PAGE_SIZE,
      });
      const mapped = response.data.map(mapPaymentListItemToPayment).filter((p): p is Payment => p !== null);
      setPending(mapped.filter((c) => c.status === "pending"));
      setInProgress(mapped.filter((c) => c.status === "in_progress"));
      setPaid(mapped.filter((c) => c.status === "paid"));
      setSkip(response.data.length);
      setHasMore(response.hasMore ?? false);
    } catch (error) {
      if (HttpApiService.isAccessDeniedError(error)) {
        setHasNoAccess(true);
        return;
      }
      const message = HttpApiService.getErrorMessage(error);
      colocaToast.error(message || t("toast_error_load"));
    }
  }, [t]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const response = await paymentsService.fetchList({
        skip,
        limit: PAYMENTS_PAGE_SIZE,
      });
      const mapped = response.data.map(mapPaymentListItemToPayment).filter((p): p is Payment => p !== null);
      const newPending = mapped.filter((c) => c.status === "pending");
      const newInProgress = mapped.filter((c) => c.status === "in_progress");
      const newPaid = mapped.filter((c) => c.status === "paid");
      setPending((prev) => [...prev, ...newPending]);
      setInProgress((prev) => [...prev, ...newInProgress]);
      setPaid((prev) => [...prev, ...newPaid]);
      setSkip((prev) => prev + response.data.length);
      setHasMore(response.hasMore ?? false);
    } catch (error) {
      const message = HttpApiService.getErrorMessage(error);
      colocaToast.error(message || t("toast_error_load_more"));
    } finally {
      setIsLoadingMore(false);
    }
  }, [skip, hasMore, isLoadingMore, t]);

  useEffect(() => {
    if (!showNewPaymentSection) {
      fetchPayments();
      fetchPendingTotals();
    }
  }, [fetchPayments, fetchPendingTotals, showNewPaymentSection]);

  useEffect(() => {
    const unsubscribe = notificationsSocketService.onNotification((notification, eventName) => {
      const isPaymentCreated = eventName === "payment:created" || notification.type === "PAYMENT_CREATED";
      if (isPaymentCreated) {
        const payload = notification as unknown as Partial<PaymentListItem>;
        if (payload.status === "PENDING" && payload.currency && payload.amount != null) {
          applyPendingTotalDelta(payload.currency, toAmountNumber(payload.amount));
        }
        fetchPayments();
        return;
      }
      if (eventName === "payment:inProgress") {
        const payload = notification as unknown as Partial<PaymentListItem>;
        const paymentId = payload?.id;
        const prior = paymentId ? pendingRef.current.find((p) => p.id === paymentId) : undefined;
        const currency = prior?.currency ?? payload.currency ?? null;
        const amount = prior ? toAmountNumber(prior.amount) : toAmountNumber(payload.amount);
        if (currency && amount > 0) {
          applyPendingTotalDelta(currency, -amount);
        }
        fetchPayments();
        return;
      }
      if (eventName === "payment:cancelled") {
        const payload = notification as unknown as {
          id?: string;
          entity_id?: string;
        };
        const paymentId = payload?.id ?? payload?.entity_id;
        if (!paymentId) return;
        const prior = pendingRef.current.find((p) => p.id === paymentId);
        if (prior) {
          applyPendingTotalDelta(prior.currency, -toAmountNumber(prior.amount));
        }
        setPending((prev) => prev.filter((p) => p.id !== paymentId));
        setInProgress((prev) => prev.filter((p) => p.id !== paymentId));
        setPaid((prev) => prev.filter((p) => p.id !== paymentId));
        setCancelledPaymentIdFromSocket(paymentId);
        if (paymentInfoPaymentIdRef.current === paymentId) setPaymentInfoPaymentId("");
        if (historyPaymentIdRef.current === paymentId) {
          setHistoryPaymentId("");
          setHistoryModalOpen(false);
        }
        if (confirmPaidForPaymentIdRef.current === paymentId) setConfirmPaidForPaymentId(null);
        if (clientNotifiedPaymentIdRef.current === paymentId) {
          setClientNotifiedPaymentId("");
          setClientNotifiedModalOpen(false);
        }
        return;
      }
      if (eventName === "payment:updated") {
        const payload = notification as unknown as PaymentListItem;
        const paymentId = payload?.id;
        if (!paymentId) return;
        const prior = pendingRef.current.find((p) => p.id === paymentId);
        const wasPending = Boolean(prior);
        const isNowPending = payload.status === "PENDING";
        if (wasPending && !isNowPending && prior) {
          applyPendingTotalDelta(prior.currency, -toAmountNumber(prior.amount));
        } else if (!wasPending && isNowPending && payload.currency) {
          applyPendingTotalDelta(payload.currency, toAmountNumber(payload.amount));
        }
        const mapped = mapPaymentListItemToPayment(payload);
        const removeId = (list: Payment[]) => list.filter((p) => p.id !== paymentId);
        setPending((prev) => removeId(prev));
        setInProgress((prev) => removeId(prev));
        setPaid((prev) => removeId(prev));
        if (mapped) {
          if (mapped.status === "pending") {
            setPending((prev) => [...prev, mapped]);
          } else if (mapped.status === "in_progress") {
            setInProgress((prev) => [...prev, mapped]);
          } else if (mapped.status === "paid") {
            setPaid((prev) => [...prev, mapped]);
          }
        }
        if (confirmPaidForPaymentIdRef.current === paymentId) setConfirmPaidForPaymentId(null);
        return;
      }
      if (eventName === NOTIFICATION_EVENTS.PAYMENT_TAKER_REMINDER) {
        const payload = notification as unknown as Partial<PaymentListItem> & {
          id?: string;
          entity_id?: string;
        };
        const paymentId = payload.id ?? payload.entity_id;
        if (!paymentId) return;
        if (payload.status == null) {
          void fetchPayments();
          void fetchPendingTotals();
          return;
        }
        const prior = pendingRef.current.find((p) => p.id === paymentId);
        const wasPending = Boolean(prior);
        const isNowPending = payload.status === "PENDING";
        if (wasPending && !isNowPending && prior) {
          applyPendingTotalDelta(prior.currency, -toAmountNumber(prior.amount));
        } else if (!wasPending && isNowPending && payload.currency) {
          applyPendingTotalDelta(payload.currency, toAmountNumber(payload.amount));
        }
        const mapped = mapPaymentListItemToPayment(payload as PaymentListItem);
        const removeId = (list: Payment[]) => list.filter((p) => p.id !== paymentId);
        setPending((prev) => removeId(prev));
        setInProgress((prev) => removeId(prev));
        setPaid((prev) => removeId(prev));
        if (mapped) {
          if (mapped.status === "pending") {
            setPending((prev) => [...prev, mapped]);
          } else if (mapped.status === "in_progress") {
            setInProgress((prev) => [...prev, mapped]);
          } else if (mapped.status === "paid") {
            setPaid((prev) => [...prev, mapped]);
          }
        }
        if (confirmPaidForPaymentIdRef.current === paymentId) setConfirmPaidForPaymentId(null);
        return;
      }
      if (eventName === "payment:filed") {
        const payload = notification as unknown as {
          id?: string;
          entity_id?: string;
        };
        const paymentId = payload?.id ?? payload?.entity_id;
        if (!paymentId) return;
        const prior = pendingRef.current.find((p) => p.id === paymentId);
        if (prior) {
          applyPendingTotalDelta(prior.currency, -toAmountNumber(prior.amount));
        }
        setPending((prev) => prev.filter((p) => p.id !== paymentId));
        setInProgress((prev) => prev.filter((p) => p.id !== paymentId));
        setPaid((prev) => prev.filter((p) => p.id !== paymentId));
        if (paymentInfoPaymentIdRef.current === paymentId) setPaymentInfoPaymentId("");
        if (historyPaymentIdRef.current === paymentId) {
          setHistoryPaymentId("");
          setHistoryModalOpen(false);
        }
        if (confirmPaidForPaymentIdRef.current === paymentId) setConfirmPaidForPaymentId(null);
        if (clientNotifiedPaymentIdRef.current === paymentId) {
          setClientNotifiedPaymentId("");
          setClientNotifiedModalOpen(false);
        }
        return;
      }
    });
    return unsubscribe;
  }, [fetchPayments, fetchPendingTotals, applyPendingTotalDelta]);

  const handleConsumedCancelledPaymentId = useCallback(() => {
    setCancelledPaymentIdFromSocket(null);
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([fetchPayments(), fetchPendingTotals()]);
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchPayments, fetchPendingTotals]);

  useEffect(() => {
    if (showNewPaymentSection && !prevShowPaymentSectionRef.current) {
      prevCurrencyRef.current = formData.tipoMoneda;
      prevBankRef.current = formData.banco;
    }
    prevShowPaymentSectionRef.current = showNewPaymentSection;
  }, [showNewPaymentSection, formData.tipoMoneda, formData.banco]);

  useEffect(() => {
    if (!showNewPaymentSection) return;
    const cur = formData.tipoMoneda;
    const bank = formData.banco;
    if (prevCurrencyRef.current !== cur || prevBankRef.current !== bank) {
      if (accountApplyLockRef.current) {
        accountApplyLockRef.current = false;
        prevCurrencyRef.current = cur;
        prevBankRef.current = bank;
        return;
      }
      setMethodFields({});
      setMethodErrors({});
    }
    prevCurrencyRef.current = cur;
    prevBankRef.current = bank;
  }, [formData.tipoMoneda, formData.banco, showNewPaymentSection]);

  useEffect(() => {
    if (!showNewPaymentSection) return;
    setSelectedPayerAccountId(null);
    setMethodFields({});
    setMethodErrors({});
    setPayerAccountSelectError(null);
  }, [formData.pagadorId, showNewPaymentSection]);

  useEffect(() => {
    if (!showNewPaymentSection || !formData.pagadorId) {
      setPayerAccounts([]);
      setPayerAccountsPhase("idle");
      setPayerAccountsLoadError(null);
      return;
    }
    const payerIsOrganization = formData.clienteId != null && formData.pagadorId === formData.clienteId;
    if (!payerIsOrganization && payerClientAccountIds.length === 0) {
      setPayerAccounts([]);
      setPayerAccountsPhase("no_ids");
      setPayerAccountsLoadError(null);
      return;
    }
    const seq = ++payerAccountsFetchSeq.current;
    setPayerAccountsPhase("loading");
    setPayerAccountsLoadError(null);
    const fetchPayerAccounts = payerIsOrganization
      ? ClientsService.fetchOrganizationAccounts(formData.pagadorId)
      : ClientsService.fetchAccounts(formData.pagadorId);
    fetchPayerAccounts
      .then((res) => {
        if (seq !== payerAccountsFetchSeq.current) return;
        const list = res.data ?? [];
        setPayerAccounts(list);
        if (list.length === 0) {
          setPayerAccountsPhase("unavailable");
          setSelectedPayerAccountId(null);
        } else {
          setPayerAccountsPhase("ready");
          setSelectedPayerAccountId((prev) => (prev && list.some((a) => a.id === prev) ? prev : null));
        }
      })
      .catch((err) => {
        if (seq !== payerAccountsFetchSeq.current) return;
        setPayerAccounts([]);
        setSelectedPayerAccountId(null);
        if (HttpApiService.isNotFoundError(err)) {
          setPayerAccountsPhase("unavailable");
          setPayerAccountsLoadError(null);
        } else {
          setPayerAccountsPhase("error");
          setPayerAccountsLoadError(HttpApiService.getErrorMessage(err) || t("toast_error_load"));
        }
      });
  }, [showNewPaymentSection, formData.pagadorId, formData.clienteId, payerClientAccountIds, t]);

  const handleSelectPayerAccountId = useCallback(
    (id: string | null) => {
      setPayerAccountSelectError(null);
      setSelectedPayerAccountId(id);
      if (!id) {
        setMethodFields({});
        return;
      }
      const acc = payerAccounts.find((a) => a.id === id);
      if (!acc) return;
      accountApplyLockRef.current = true;
      const { tipoMoneda, banco } = resolvePaymentBankAndCurrency(acc);
      setFormData((prev) => ({ ...prev, tipoMoneda, banco }));
      setMethodFields(paymentMethodFieldsFromAccountMetadata(acc, tipoMoneda, banco));
      setMethodErrors({});
    },
    [payerAccounts],
  );

  const resetNewPaymentFormState = useCallback(() => {
    setFormData(getDefaultPaymentFormData());
    setMethodFields({});
    setCommonErrors({});
    setMethodErrors({});
    setPayerClientAccountIds([]);
    setPayerAccounts([]);
    setPayerAccountsPhase("idle");
    setPayerAccountsLoadError(null);
    setSelectedPayerAccountId(null);
    setPayerAccountSelectError(null);
  }, []);

  const handleNewPayment = useCallback(() => {
    resetNewPaymentFormState();
    setShowNewPaymentSection(true);
  }, [resetNewPaymentFormState]);

  const handleClearNewPaymentForm = useCallback(() => {
    if (isSubmitting) return;
    resetNewPaymentFormState();
  }, [isSubmitting, resetNewPaymentFormState]);

  const handleCloseNewPaymentSection = useCallback(() => {
    if (!isSubmitting) {
      resetNewPaymentFormState();
      setShowNewPaymentSection(false);
    }
  }, [isSubmitting, resetNewPaymentFormState]);

  const handleMethodFieldChange = useCallback((fieldId: string, value: string) => {
    setMethodFields((prev) => ({ ...prev, [fieldId]: value }));
    setMethodErrors((prev) => {
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  const handleSubmitNewPayment = useCallback(
    async (data: NewPaymentFormFullData) => {
      const created = await paymentsService.create(data);
      if (created.status === "PENDING" && created.currency) {
        applyPendingTotalDelta(created.currency, toAmountNumber(created.amount));
      }
      colocaToast.success(t("toast_success_create"));
      handleCloseNewPaymentSection();
      await fetchPayments();
    },
    [handleCloseNewPaymentSection, fetchPayments, t, applyPendingTotalDelta],
  );

  const handleSubmit = useCallback(async () => {
    setPayerAccountSelectError(null);

    const accountsFetchPending = Boolean(formData.pagadorId) && payerAccountsPhase === "loading";
    if (accountsFetchPending) {
      return;
    }

    const commonValidation = validateNewConfirmationForm(formData, {
      requireComprobante: false,
      requireClientAndPayer: true,
      requireClientAndPayerIds: true,
    });
    const methodValidation = validatePaymentMethodFields(formData.tipoMoneda, formData.banco, methodFields);
    const hasCommonErrors = Object.keys(commonValidation).length > 0;
    const hasMethodErrors = Object.keys(methodValidation).length > 0;

    if (formData.pagadorId) {
      if (payerAccountsPhase === "no_ids" || payerAccountsPhase === "unavailable") {
        colocaToast.error(t("form_payer_accounts_missing"));
        return;
      }
      if (payerAccountsPhase === "error") {
        colocaToast.error(payerAccountsLoadError || t("toast_error_load"));
        return;
      }
      if (payerAccountsPhase === "ready" && payerAccounts.length > 0 && !selectedPayerAccountId) {
        setPayerAccountSelectError(t("validation_payer_account_required"));
        return;
      }
    }

    if (hasCommonErrors || hasMethodErrors) {
      const translatedCommon = translateNewConfirmationErrors(tConfirmations, commonValidation);
      const translatedMethod: Partial<Record<string, string>> = {};
      for (const [key, value] of Object.entries(methodValidation)) {
        if (value) translatedMethod[key] = value;
      }
      setCommonErrors(translatedCommon);
      setMethodErrors(translatedMethod);
      return;
    }
    setCommonErrors({});
    setMethodErrors({});
    setIsSubmitting(true);
    try {
      await handleSubmitNewPayment({
        common: formData,
        methodFields,
        ...(formData.pagadorId === formData.clienteId
          ? { organization_id: formData.pagadorId }
          : { client_id: formData.pagadorId }),
      });
    } catch (error) {
      const message = HttpApiService.getErrorMessage(error);
      colocaToast.error(message || t("toast_error_create"));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formData,
    methodFields,
    handleSubmitNewPayment,
    payerAccountsPhase,
    payerAccounts.length,
    payerAccountsLoadError,
    selectedPayerAccountId,
    t,
    tConfirmations,
  ]);

  const handleStatusChange = useCallback(
    async (
      payment: Payment,
      newStatus: PaymentStatus,
      attachments?: File[],
      urlHas?: string,
      paymentSource?: string,
    ) => {
      try {
        if (newStatus === "paid") {
          // One atomic call: the receipts are stored before the status flips, so a
          // failure here leaves the payment in En proceso instead of marking it paid
          // with partial or missing proof.
          await paymentsService.markAsPaid(payment.id, {
            paymentSource: paymentSource?.trim() ?? "",
            urlHas,
            files: attachments,
          });
        } else {
          const apiStatus = paymentStatusToApi(newStatus);
          await paymentsService.updateStatus(payment.id, apiStatus);
        }
        colocaToast.success(t("toast_success_status"));
      } catch (error) {
        const message = HttpApiService.getErrorMessage(error);
        colocaToast.error(message || t("toast_error_status"));
        throw error;
      } finally {
        await Promise.all([fetchPayments(), fetchPendingTotals()]);
      }
    },
    [fetchPayments, fetchPendingTotals, t],
  );

  const handleCancel = useCallback(
    async (id: string, reason?: string) => {
      const note = reason?.trim() ?? "";
      if (!note) return;
      try {
        await paymentsService.cancel(id, note);
        colocaToast.success(t("toast_success_cancel"));
        await fetchPayments();
      } catch (error) {
        const message = HttpApiService.getErrorMessage(error);
        colocaToast.error(message || t("toast_error_cancel"));
        throw error;
      }
    },
    [fetchPayments, t],
  );

  /**
   * Taking a payment is ONE atomic backend call: it assigns the taker and moves the payment to IN_PROGRESS
   * together. It used to be `take` + `updateStatus`, and a failure between the two left the payment locked
   * while still sitting in Pendientes, with no way back from the UI.
   *
   * The card is not moved optimistically on purpose: the confirm modal already shows a loading state, and
   * refetching from the server avoids the card jumping to En proceso and snapping back when something goes
   * wrong.
   */
  const handleTake = useCallback(
    async (id: string) => {
      try {
        await paymentsService.take(id);
        colocaToast.success(t("toast_success_take"));
      } catch (error) {
        const message = HttpApiService.getErrorMessage(error);
        colocaToast.error(message || t("toast_error_take"));
        throw error;
      } finally {
        await Promise.all([fetchPayments(), fetchPendingTotals()]);
      }
    },
    [fetchPayments, fetchPendingTotals, t],
  );

  /** Hands the payment back to the queue so another operator can take it. */
  const handleRelease = useCallback(
    async (id: string, force = false) => {
      try {
        await (force ? paymentsService.forceRelease(id) : paymentsService.release(id));
        colocaToast.success(t("toast_success_release"));
      } catch (error) {
        const message = HttpApiService.getErrorMessage(error);
        colocaToast.error(message || t("toast_error_release"));
        throw error;
      } finally {
        await Promise.all([fetchPayments(), fetchPendingTotals()]);
      }
    },
    [fetchPayments, fetchPendingTotals, t],
  );

  const handleViewHistory = useCallback((id: string) => {
    setHistoryPaymentId(id);
    setHistoryModalOpen(true);
  }, []);

  const handleViewPaymentInfo = useCallback((id: string) => {
    setPaymentInfoPaymentId(id);
  }, []);

  const paymentForInfo = paymentInfoPaymentId
    ? [...pending, ...inProgress, ...paid].find((p) => p.id === paymentInfoPaymentId)
    : undefined;

  const handleCloseHistoryModal = useCallback(() => {
    setHistoryModalOpen(false);
    setHistoryPaymentId("");
  }, []);

  const handleClosePaymentInfoModal = useCallback(() => {
    setPaymentInfoPaymentId("");
  }, []);

  const handleConfirmPaymentFromInfo = useCallback(() => {
    if (!paymentInfoPaymentId) return;
    setConfirmPaidForPaymentId(paymentInfoPaymentId);
    setPaymentInfoPaymentId("");
  }, [paymentInfoPaymentId]);

  const handleConfirmPaidModalClose = useCallback(() => {
    setConfirmPaidForPaymentId(null);
  }, []);

  const handleNotifyClient = useCallback((id: string) => {
    setClientNotifiedPaymentId(id);
    setClientNotifiedModalOpen(true);
  }, []);

  const handleTakerReminder = useCallback(
    async (id: string) => {
      try {
        await paymentsService.sendTakerReminder(id);
        colocaToast.success(t("toast_success_taker_reminder"));
      } catch (error) {
        const message = HttpApiService.getErrorMessage(error);
        colocaToast.error(message || t("toast_error_taker_reminder"));
        throw error;
      }
    },
    [t],
  );

  const handleCloseClientNotifiedModal = useCallback(() => {
    setClientNotifiedModalOpen(false);
    setClientNotifiedPaymentId("");
  }, []);

  const handleConfirmClientNotified = useCallback(async () => {
    if (!clientNotifiedPaymentId) return;
    try {
      await paymentsService.updateStatus(clientNotifiedPaymentId, "FILED");
      colocaToast.success(t("toast_success_notify"));
      handleCloseClientNotifiedModal();
      await fetchPayments();
    } catch (error) {
      const message = HttpApiService.getErrorMessage(error);
      colocaToast.error(message || t("toast_error_notify"));
      throw error;
    }
  }, [clientNotifiedPaymentId, handleCloseClientNotifiedModal, fetchPayments, t]);

  const handleView = useCallback((_id: string, imageUrls?: string[] | null) => {
    const urls = imageUrls?.length ? imageUrls : [];
    setGalleryImageUrls(urls);
    setGalleryInitialIndex(0);
    setGalleryOpen(true);
  }, []);

  if (hasNoAccess) {
    return <NoAccessView />;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          {t("page_title")} (Payouts)
        </h1>
        {showNewPaymentSection ? (
          <Button
            type="button"
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={handleCloseNewPaymentSection}
            disabled={isSubmitting}
            className="shadow-sm border-2 border-green-500 text-green-600 hover:bg-green-500 hover:text-white dark:border-green-400 dark:text-green-300 dark:hover:bg-green-500 dark:hover:text-white active:scale-[0.98] w-full sm:w-auto shrink-0"
          >
            {t("button_back_to_list")}
          </Button>
        ) : (
          <Button
            type="button"
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={handleNewPayment}
            className="shadow-sm active:scale-[0.98] w-full sm:w-auto shrink-0"
          >
            {t("button_new_payment")}
          </Button>
        )}
      </div>

      {showNewPaymentSection ? (
        <section
          className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 shadow-sm overflow-visible min-h-0"
          aria-labelledby="new-payment-heading"
        >
          <h2 id="new-payment-heading" className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
            {t("section_new_payment_title")}
          </h2>
          <div className="space-y-8">
            <NewConfirmationForm
              value={formData}
              onChange={setFormData}
              errors={commonErrors}
              disabled={isSubmitting}
              hideComprobante
              disableCurrencyBankDateFields={true}
              disableAmountNoteFields={!selectedPayerAccountId}
              clientPayerSection={
                <div className="space-y-4">
                  <PaymentClientPayerFields
                    value={formData}
                    onChange={setFormData}
                    errors={commonErrors}
                    disabled={isSubmitting}
                    fetchEnabled={showNewPaymentSection}
                    onPayerAccountIdsChange={handlePayerAccountIdsChange}
                  />
                  <PaymentPayerAccountSection
                    pagadorId={formData.pagadorId}
                    phase={payerAccountsPhase}
                    accounts={payerAccounts}
                    loadErrorMessage={payerAccountsLoadError}
                    selectedAccountId={selectedPayerAccountId}
                    onSelectAccountId={handleSelectPayerAccountId}
                    disabled={isSubmitting}
                    label={t("form_payer_account_label")}
                    placeholder={t("form_payer_account_placeholder")}
                    missingAccountsMessage={t("form_payer_accounts_missing")}
                    loadingMessage={t("form_payer_accounts_loading")}
                    selectError={payerAccountSelectError ?? undefined}
                    goToClientsButtonLabel={t("form_client_search_go_to_clients")}
                  />
                </div>
              }
            />
            <PaymentMethodFieldsSection
              currency={formData.tipoMoneda}
              method={formData.banco}
              value={methodFields}
              onChange={handleMethodFieldChange}
              errors={methodErrors}
              disabled
            />
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClearNewPaymentForm}
                disabled={isSubmitting}
                className="shadow-sm active:scale-[0.98]"
              >
                {t("button_clear_form")}
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  !selectedPayerAccountId ||
                  !isValidMontoValue(formData.monto) ||
                  (Boolean(formData.pagadorId) && payerAccountsPhase === "loading")
                }
                isLoading={isSubmitting}
                className="shadow-sm active:scale-[0.98]"
              >
                {t("button_create_payment")}
              </Button>
              <button
                type="button"
                onClick={handleCloseNewPaymentSection}
                disabled={isSubmitting}
                className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 text-sm font-medium transition-colors"
              >
                {t("button_cancel")}
              </button>
            </div>
          </div>
        </section>
      ) : (
        <>
          <PendingTotalsSummary
            totals={pendingTotals}
            isLoading={pendingTotalsLoading}
            errorMessage={pendingTotalsError}
            onRefresh={fetchPendingTotals}
          />
          <PaymentsBoard
            pending={pending}
            inProgress={inProgress}
            paid={paid}
            onStatusChange={handleStatusChange}
            onCancel={handleCancel}
            onTake={handleTake}
            onRelease={handleRelease}
            currentUserId={user?.id}
            canForceRelease={canForceRelease}
            onViewHistory={handleViewHistory}
            onViewPaymentInfo={handleViewPaymentInfo}
            onNotifyClient={handleNotifyClient}
            onView={handleView}
            onTakerReminder={handleTakerReminder}
            confirmPaidForPaymentId={confirmPaidForPaymentId}
            onConfirmPaidModalClose={handleConfirmPaidModalClose}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
            cancelledPaymentIdFromSocket={cancelledPaymentIdFromSocket}
            onConsumedCancelledPaymentId={handleConsumedCancelledPaymentId}
          />
          {hasMore && (
            <div className="flex justify-center pt-2 pb-4">
              <button
                type="button"
                onClick={loadMore}
                disabled={isLoadingMore}
                className="flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 disabled:opacity-50 transition-colors"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                    {t("loading")}
                  </>
                ) : (
                  t("show_more")
                )}
              </button>
            </div>
          )}
        </>
      )}

      <AttachmentsGalleryModal
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        imageUrls={galleryImageUrls}
        initialIndex={galleryInitialIndex}
        alt={t("alt_comprobante_pago")}
      />

      <PaymentHistoryModal
        key={historyPaymentId || "closed"}
        isOpen={historyModalOpen}
        onClose={handleCloseHistoryModal}
        paymentId={historyPaymentId}
      />

      <PaymentInfoModal
        isOpen={Boolean(paymentInfoPaymentId)}
        onClose={handleClosePaymentInfoModal}
        paymentName={paymentForInfo ? (paymentForInfo.payer ?? "") : ""}
        amount={paymentForInfo?.amount}
        currency={paymentForInfo?.currency}
        bankMetadata={paymentForInfo ? paymentForInfo.bankMetadata : undefined}
        bank={paymentForInfo?.platform}
        onConfirmPayment={handleConfirmPaymentFromInfo}
      />

      <ClientNotifiedModal
        isOpen={clientNotifiedModalOpen}
        onClose={handleCloseClientNotifiedModal}
        onConfirm={handleConfirmClientNotified}
      />
    </div>
  );
};
